// Banner and Header Variants Configuration
//
// MANDATORY when adding a variant here: give it a human-readable `name`. It is
// spread into SECTION_VARIANTS by ./index.ts and read through
// `getVariantDetails()` by the page builder and by **Widget Management**
// (`/admin/widget-management`), where that string is the only label identifying
// the widget before someone deletes it from live pages. Miss it and the screen
// falls back to the raw id.
//
// Full checklist for a new section or variant: top of ../sectionTypes.ts.

import { getActiveCourses } from "@/api/admin/course";
import { Form_1 } from "@/components/organisms/admin/forms";
import {
	setBanner1,
	setBanner10,
	setBanner1010,
	setBanner1025,
	setBanner1035,
	setBanner11,
	setBanner15,
	setBanner2,
	setBanner3,
	setBanner3000,
	setBanner3001,
	setBanner5004,
	setBanner1051,
	setBanner1058,
	setBanner1059,
	setBanner1061,
	setBanner1062,
	setBanner1068,
	setBanner4,
	setBanner5,
	setBanner6,
	setBanner6000,
	setBanner8,
	setBanner9,
	setBannerVideo1,
	setEnrollments1,
	setEnrollments2,
	setEnrollments3,
	setEnrollments4,
	setEnrollments5,
	setHeader1,
	setHeader1046,
	setHeader2,
	setHeader3,
	setHeader4,
	setHeader5,
	setHeader5000,
	setHeader5001,
	setHeader5016,
	setHeader5015,
	setHeader6,
	setHeader7,
	setLifeMilestone1,
	setLifeMilestone2,
	setLifeMilestone3,
	setWalkThrough1,
} from "@/store/slices/page";
import Banner1 from "@/widgets/banner/Banner1";
import Banner10 from "@/widgets/banner/Banner10";
import Banner1010 from "@/widgets/banner/Banner1010";
import Banner1025 from "@/widgets/banner/Banner1025";
import Banner1035 from "@/widgets/banner/Banner1035";
import Banner11 from "@/widgets/banner/Banner11";
import Banner15 from "@/widgets/banner/Banner15";
import Banner2 from "@/widgets/banner/Banner2";
import Banner3 from "@/widgets/banner/Banner3";
import Banner3000 from "@/widgets/banner/Banner3000";
import Banner3001 from "@/widgets/banner/Banner3001";
import Banner5004 from "@/widgets/banner/Banner5004";
import Banner1051 from "@/widgets/banner/Banner1051";
import Banner1058 from "@/widgets/banner/Banner1058";
import Banner1059 from "@/widgets/banner/Banner1059";
import Banner1061 from "@/widgets/banner/Banner1061";
import Banner1062 from "@/widgets/banner/Banner1062";
import Banner1068 from "@/widgets/banner/Banner1068";
import Banner5 from "@/widgets/banner/Banner5";
import Banner6000 from "@/widgets/banner/Banner6000";
import Banner8 from "@/widgets/banner/Banner8";
import Banner9 from "@/widgets/banner/Banner9";
import BannerFeatureGrid from "@/widgets/banner/BannerFeatureGrid";
import HeroCarousel from "@/widgets/banner/HeroCarousel";
import BannerVideo1 from "@/widgets/bannerVideo/BannerVideo1";
import Enrollments1 from "@/widgets/enrollments/Enrollments1";
import Enrollments2 from "@/widgets/enrollments/Enrollments2";
import Enrollments3 from "@/widgets/enrollments/Enrollments3";
import Enrollments4 from "@/widgets/enrollments/Enrollments4";
import Enrollments5 from "@/widgets/enrollments/Enrollments5";
import Header1 from "@/widgets/header/Header1";
import Header2 from "@/widgets/header/Header2";
import LifeMilestone1 from "@/widgets/lifeMilestone/LifeMilestone1";
import LifeMilestone2 from "@/widgets/lifeMilestone/LifeMilestone2";
import LifeMilestone3 from "@/widgets/lifeMilestone/LifeMilestone3";
import WalkThrough1 from "@/widgets/walkThrough/WalkThrough1";
import { BANNER_1_CONSTANTS } from "../fields/banner/banner_1";
import { BANNER_10_CONSTANTS } from "../fields/banner/banner_10";
import { BANNER_1010_CONSTANTS } from "../fields/banner/banner_1010";
import { BANNER_1025_CONSTANTS } from "../fields/banner/banner_1025";
import { BANNER_1035_CONSTANTS } from "../fields/banner/banner_1035";
import { BANNER_11_CONSTANTS } from "../fields/banner/banner_11";
import { BANNER_15_CONSTANTS } from "../fields/banner/banner_15";
import { BANNER_2_CONSTANTS } from "../fields/banner/banner_2";
import { BANNER_3_CONSTANTS } from "../fields/banner/banner_3";
import { BANNER_3000_CONSTANTS } from "../fields/banner/banner_3000";
import { BANNER_3001_CONSTANTS } from "../fields/banner/banner_3001";
import { BANNER_5004_CONSTANTS } from "../fields/banner/banner_5004";
import { BANNER_1051_CONSTANTS } from "../fields/banner/banner_1051";
import { BANNER_1058_CONSTANTS } from "../fields/banner/banner_1058";
import { BANNER_1059_CONSTANTS } from "../fields/banner/banner_1059";
import { BANNER_1061_CONSTANTS } from "../fields/banner/banner_1061";
import { BANNER_1062_CONSTANTS } from "../fields/banner/banner_1062";
import { BANNER_1068_CONSTANTS } from "../fields/banner/banner_1068";
import { BANNER_4_CONSTANTS } from "../fields/banner/banner_4";
import {
	BANNER_5_CONSTANTS,
	BANNER_5_DEFAULT_FEATURE_ROWS,
} from "../fields/banner/banner_5";
import {
	BANNER_6_CONSTANTS,
	BANNER_6_DEFAULT_GRID_ROWS,
	buildBanner6PersistPayload,
} from "../fields/banner/banner_6";
import { BANNER_6000_CONSTANTS } from "../fields/banner/banner_6000";
import { BANNER_8_CONSTANTS } from "../fields/banner/banner_8";
import { BANNER_9_CONSTANTS } from "../fields/banner/banner_9";
import { BANNER_VIDEO_1_CONSTANTS } from "../fields/bannerVideo/bannerVideo_1";
import { ENROLLMENTS_1_CONSTANTS } from "../fields/enrollments/enrollments_1";
import { ENROLLMENTS_2_CONSTANTS } from "../fields/enrollments/enrollments_2";
import { ENROLLMENTS_3_CONSTANTS } from "../fields/enrollments/enrollments_3";
import { ENROLLMENTS_4_CONSTANTS } from "../fields/enrollments/enrollments_4";
import { ENROLLMENTS_5_CONSTANTS } from "../fields/enrollments/enrollments_5";
import { HEADER_1_CONSTANTS } from "../fields/header/header_1";
import { HEADER_1046_CONSTANTS } from "../fields/header/header_1046";
import { HEADER_2_CONSTANTS } from "../fields/header/header_2";
import { HEADER_3_CONSTANTS } from "../fields/header/header_3";
import { HEADER_4_CONSTANTS } from "../fields/header/header_4";
import { HEADER_5_CONSTANTS } from "../fields/header/header_5";
import { HEADER_5001_CONSTANTS } from "../fields/header/header_5001";
import { HEADER_5016_CONSTANTS } from "../fields/header/header_5016";
import { HEADER_5015_CONSTANTS } from "../fields/header/header_5015";
import { HEADER_6_CONSTANTS } from "../fields/header/header_6";
import { HEADER_7_CONSTANTS } from "../fields/header/header_7";
// import Header3 from "@/widgets/header/Header3";
import { createBanner, updateBanner } from "@/api/admin/banner";
import { createBannerVideo, updateBannerVideo } from "@/api/admin/bannerVideo";
import { createEnrollments, updateEnrollments } from "@/api/admin/enrollments";
import { createHeader, updateHeader } from "@/api/admin/header";
import {
	createLifeMilestone,
	updateLifeMilestone,
} from "@/api/admin/lifeMilestone";
import { createWalkThrough, updateWalkThrough } from "@/api/admin/walkThrough";
import { extractMetadataFields } from "@/helpers";
import type { PageState } from "@/store/slices/page";
import {
	activateMetadata,
	getPreviewData,
	isCmsShowFlagOn,
} from "@/utils/variantHelpers";
import Header1046 from "@/widgets/header/Header1046";
import Header3 from "@/widgets/header/Header3";
import Header4 from "@/widgets/header/Header4";
import Header5 from "@/widgets/header/Header5";
import Header5001 from "@/widgets/header/Header5001";
import Header5016 from "@/widgets/header/Header5016";
import Header5015 from "@/widgets/header/Header5015";
import Header6 from "@/widgets/header/Header6";
import Header7 from "@/widgets/header/Header7";
import PromoPill1 from "@/widgets/promoPill/PromoPill1";
import { LIFE_MILESTONE_1_CONSTANTS } from "../fields/lifeMilestone/lifeMilestone_1";
import { LIFE_MILESTONE_2_CONSTANTS } from "../fields/lifeMilestone/lifeMilestone_2";
import { LIFE_MILESTONE_3_CONSTANTS } from "../fields/lifeMilestone/lifeMilestone_3";
import { PROMO_PILL_1_CONSTANTS } from "../fields/promoPill/promoPill_1";
import { WALK_THROUGH_1_CONSTANTS } from "../fields/walkThrough/walkThrough_1";
import {
	BANNER_VARIANTS,
	BANNER_VIDEO_VARIANTS,
	ENROLLMENTS_VARIANTS,
	HEADER_VARIANTS,
	LIFE_MILESTONE_VARIANTS,
	SECTION_TYPES,
	WALK_THROUGH_VARIANTS,
} from "../sectionTypes";

export const BANNER_SECTION_VARIANTS = {
	[SECTION_TYPES.BANNER]: [
		{
			id: BANNER_VARIANTS.BANNER_1,
			name: "Banner 1",
			description: "Banner 1",
			componentFields: BANNER_1_CONSTANTS.BANNER_FIELDS_CONFIG,
			...extractMetadataFields(BANNER_1_CONSTANTS),
			showTable: true,
			setAction: setBanner1,
			formComponent: Form_1,
			previewComponent: Banner1,
			selector: (state: PageState) => state.banner_1,
			createMethod: createBanner,
			updateMethod: updateBanner,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						// data: {
						metadata: activateMetadata(data.metadata),
						metadata_1: activateMetadata(data.metadata_1),
						learners_description: data.learners_description || "",
						learners_description_color:
							data.learners_description_color || "#000000",
						learners_status: data.learners_status || false,
						slide_frequency: data.slide_frequency || 5000,
						status: true,
						// },
					},
				};
			},
		},
		{
			id: BANNER_VARIANTS.BANNER_2,
			name: "Banner 2",
			description: "Banner 2 - Centered Layout",
			...extractMetadataFields(BANNER_2_CONSTANTS),
			componentFields: BANNER_2_CONSTANTS.BANNER_FIELDS_CONFIG,
			showTable: true,
			setAction: setBanner2,
			formComponent: Form_1,
			previewComponent: Banner2,
			selector: (state: PageState) => state.banner_2,
			createMethod: createBanner,
			updateMethod: updateBanner,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						metadata: activateMetadata(data.metadata),
						metadata_1: activateMetadata(data.metadata_1),
						metadata_2: activateMetadata(data.metadata_2),
						metadata_3: activateMetadata(data.metadata_3),
						slide_frequency: data.slide_frequency || 5000,
						status: true,
						// Banner 2 specific fields
						title_1: data.title_1 || "Welcome to",
						title_1_color: data.title_1_color || "#FFFFFF",
						title_2: data.title_2 || "Master of Business Administration",
						title_2_gradient_start_color:
							data.title_2_gradient_start_color || "rgba(255, 215, 0, 1)",
						title_2_gradient_end_color:
							data.title_2_gradient_end_color || "rgba(255, 165, 0, 1)",
						title_2_gradient_angle: data.title_2_gradient_angle || "90deg",
						subtitle:
							data.subtitle ||
							"Transform Your Career with Industry-Leading Education",
						subtitle_color: data.subtitle_color || "#E0E0E0",
						horizontal_stripe_color: data.horizontal_stripe_color || "#BE2330",
						font_family_variant: data.font_family_variant || "variant_2",
						backvariant: data.backvariant || "variant_1",
						gradient_color_1:
							data.gradient_color_1 || "rgba(0, 212, 235, 0.33)",
						gradient_color_2: data.gradient_color_2 || "rgba(0, 0, 0, 0.66)",
						gradient_color_3: data.gradient_color_3 || "rgba(0, 0, 0, 0.66)",
						gradient_color_4:
							data.gradient_color_4 || "rgba(83, 69, 195, 0.46)",
						background_color: data.background_color || "#000000",
						learners_description:
							data.learners_description ||
							"<p><strong>10,000+</strong> Learners already enrolled</p>",
						carousel_color: data.carousel_color || "#FFFFFF",
						accreditation_scroll_duration:
							data.accreditation_scroll_duration || 20000,
						rating_number: data.rating_number || "4/5",
						rating_text: data.rating_text || "Google Rating",
						rating_color: data.rating_color || "#FFFFFF",
						rating_number_color: data.rating_number_color || "#FFFFFF",
						download_brochure_text:
							data.download_brochure_text || "Download Brochure",
						button_color: data.button_color || "#BE2330",
						icon: data.icon || "",
						alt_icon: data.alt_icon || "Download icon",
						icon_color: data.icon_color || "#FFFFFF",
						apply_now_text: data.apply_now_text || "Apply Now",
						apply_now_color: data.apply_now_color || "#FFFFFF",
						apply_now_border_color: data.apply_now_border_color || "#FFFFFF",
					},
				};
			},
		},
		{
			id: BANNER_VARIANTS.BANNER_3,
			name: "Banner 3",
			description: "Banner with parallax video hero",
			...extractMetadataFields(BANNER_3_CONSTANTS),
			componentFields: BANNER_3_CONSTANTS.BANNER_FIELDS_CONFIG,
			showTable: true,
			setAction: setBanner3,
			formComponent: Form_1,
			previewComponent: Banner3,
			selector: (state: PageState) => state.banner_3,
			createMethod: createBanner,
			updateMethod: updateBanner,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						metadata: activateMetadata(data.metadata),
						slide_frequency:
							typeof data.slide_frequency === "number"
								? data.slide_frequency
								: Number(data.slide_frequency) || 5000,
						hero_video_url: data.hero_video_url || "",
						accreditation_image_desktop: data.accreditation_image_desktop || "",
						accreditation_image_mobile: data.accreditation_image_mobile || "",
						title_1: data.title_1 || "Education That",
						title_1_color: data.title_1_color || "rgba(255,255,255,0.8)",
						title_2: data.title_2 || "Transforms",
						title_2_gradient_start_color:
							data.title_2_gradient_start_color || "#342B7C",
						title_2_gradient_end_color:
							data.title_2_gradient_end_color || "#02A7B6",
						title_2_gradient_angle: data.title_2_gradient_angle || "90deg",
						subtitle:
							data.subtitle ||
							"We University brings premium, industry-aligned learning online. Build skills that matter, work on real challenges, and unlock your next big move.",
						subtitle_color: data.subtitle_color || "#FFFFFF",
						apply_now_text: data.apply_now_text || "Apply Now",
						apply_now_color: data.apply_now_color || "#222222",
						button_color: data.button_color || "#FFFFFF",
						explore_cta_url: data.explore_cta_url ?? "",
						background_color: data.background_color || "#000000",
						status: true,
					},
				};
			},
		},
		{
			id: BANNER_VARIANTS.BANNER_4,
			name: "Banner 4 - HeroCarousel",
			description: "Banner 4 - HeroCarousel",
			...extractMetadataFields(BANNER_4_CONSTANTS),
			componentFields: BANNER_4_CONSTANTS.BANNER_FIELDS_CONFIG,
			showTable: true,
			setAction: setBanner4,
			formComponent: Form_1,
			previewComponent: HeroCarousel,
			selector: (state: PageState) => state.banner_4,
			createMethod: createBanner,
			updateMethod: updateBanner,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						// Do not use activateMetadata: HeroCarousel hides slides with status false; preview must match live.
						metadata: Array.isArray(data.metadata) ? data.metadata : [],
						metadata_1: Array.isArray(data.metadata_1) ? data.metadata_1 : [],
						slide_frequency: data.slide_frequency ?? 7000,
						hero_section_height: data.hero_section_height ?? 564,
						hero_container_max_width: data.hero_container_max_width ?? 1114,
						background_color: data.background_color || "#291973",
						hero_info_bar_show: isCmsShowFlagOn(data.hero_info_bar_show),
						hero_info_bar_text: data.hero_info_bar_text ?? "",
						apply_now_show: isCmsShowFlagOn(data.apply_now_show),
						apply_now_text: data.apply_now_text || "Get in Touch",
						apply_now_link: data.apply_now_link || "",
						download_brochure_show: isCmsShowFlagOn(
							data.download_brochure_show,
						),
						download_brochure_text:
							data.download_brochure_text || "Download Brochure",
						download_brochure_link: data.download_brochure_link || "",
						application_deadline_show: isCmsShowFlagOn(
							data.application_deadline_show,
						),
						application_deadline_text: data.application_deadline_text ?? "",
						button_color: data.button_color || "#291973",
						apply_now_color: data.apply_now_color || "#ffffff",
						carousel_color: data.carousel_color || "#ffffff",
						status: isCmsShowFlagOn(data.status, true),
					},
				};
			},
		},
		{
			id: BANNER_VARIANTS.BANNER_5,
			name: "Banner 5 — Hero feature grid",
			description: "Banner 5 — Hero feature grid",
			...extractMetadataFields(BANNER_5_CONSTANTS),
			componentFields: BANNER_5_CONSTANTS.BANNER_FIELDS_CONFIG,
			showTable: true,
			setAction: setBanner5,
			formComponent: Form_1,
			previewComponent: BannerFeatureGrid,
			selector: (state: PageState) => state.banner_5,
			createMethod: createBanner,
			updateMethod: updateBanner,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				const features =
					Array.isArray(data.metadata) && data.metadata.length > 0
						? data.metadata
						: BANNER_5_DEFAULT_FEATURE_ROWS;
				const bgRows =
					Array.isArray(data.metadata_1) && data.metadata_1.length > 0
						? data.metadata_1
						: [BANNER_5_CONSTANTS.DEFAULT_ROW_1];
				return {
					data: {
						metadata: activateMetadata(features),
						metadata_1: activateMetadata(bgRows),
						hero_section_height: data.hero_section_height ?? 720,
						hero_container_max_width: data.hero_container_max_width ?? 1200,
						title_1:
							data.title_1 ||
							"From Full Stack to AI-Native Systems, {led by IIT Kharagpur's Computer Science & Engineering Department}",
						title_1_color: data.title_1_color || "#FFFFFF",
						subtitle_color: data.subtitle_color || "#F5C451",
						gradient_color_1: data.gradient_color_1 || "rgba(0, 0, 0, 0.55)",
						gradient_color_2: data.gradient_color_2 || "rgba(0, 0, 0, 0.78)",
						background_color: data.background_color || "#0a1628",
						status: data.status !== false,
					},
				};
			},
		},
		{
			id: BANNER_VARIANTS.BANNER_6,
			name: "Banner 6 — Capstone split + lead form",
			description: "Banner 6 — Capstone split + lead form",
			...extractMetadataFields(BANNER_6_CONSTANTS),
			componentFields: BANNER_6_CONSTANTS.BANNER_FIELDS_CONFIG,
			showTable: true,
			setAction: setBanner6,
			formComponent: Form_1,
			previewComponent: Banner5,
			selector: (state: PageState) => state.banner_6,
			createMethod: createBanner,
			updateMethod: updateBanner,
			transformPayload: buildBanner6PersistPayload,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				const grid =
					Array.isArray(data.metadata) && data.metadata.length > 0
						? data.metadata
						: BANNER_6_DEFAULT_GRID_ROWS;
				const bgRows =
					Array.isArray(data.metadata_2) && data.metadata_2.length > 0
						? data.metadata_2
						: [];
				const bg0 = bgRows[0];
				return {
					data: {
						metadata: activateMetadata(grid),
						metadata_2: activateMetadata(bgRows),
						background_image:
							data.background_image ?? (bg0 as { image?: string })?.image ?? "",
						alt_background_image:
							data.alt_background_image ??
							(bg0 as { alt_image?: string })?.alt_image ??
							"",
						hero_section_height: data.hero_section_height ?? 0,
						hero_container_max_width: data.hero_container_max_width ?? 1200,
						title_1:
							data.title_1 || "Capstone: Build One System. Ship to Production",
						title_1_color: data.title_1_color || "#FFFFFF",
						subtitle:
							data.subtitle ||
							"Choose a real-world problem from your domain - fintech, e-commerce, healthcare, or enterprise SaaS, and build a complete AI-native system from architecture to deployment.",
						subtitle_color: data.subtitle_color || "rgba(255,255,255,0.92)",
						learners_description:
							data.learners_description ||
							"Design, build, and deploy an end-to-end application integrating frontend, backend, AI systems, and agents - demonstrating true production-grade engineering capability.",
						icon_color: data.icon_color || "#F5C451",
						button_color: data.button_color || "#E85D04",
						apply_now_text: data.apply_now_text || "Apply Now",
						apply_now_color: data.apply_now_color || "#FFFFFF",
						background_color: data.background_color || "#0a0a0a",
						status: data.status !== false,
					},
				};
			},
		},
		{
			id: BANNER_VARIANTS.BANNER_8,
			name: "Banner 8 — Background image + info-cards grid",
			description:
				"Banner 8 — Background image + info-cards grid.",
			...extractMetadataFields(BANNER_8_CONSTANTS),
			componentFields: BANNER_8_CONSTANTS.BANNER_FIELDS_CONFIG,
			showTable: false,
			setAction: setBanner8,
			formComponent: Form_1,
			previewComponent: Banner8,
			selector: (state: PageState) => state.banner_8,
			createMethod: createBanner,
			updateMethod: updateBanner,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						...data,
						status: data.status !== false,
						title: data.title ?? "Key {skills} you'll build",
						description: data.description ?? "",
						image: data.image ?? "",
						mobile_image: data.mobile_image ?? "",
						image_alt: data.image_alt ?? "",
						background_color: data.background_color ?? "#000000",
						title_color: data.title_color ?? "#FFFFFF",
						title_highlight_color: data.title_highlight_color ?? "#FFD700",
						description_color: data.description_color ?? "#FFFFFF",
						card_title_color: data.card_title_color ?? "#FFFFFF",
						card_description_color: data.card_description_color ?? "#FFFFFF",
						divider_color: data.divider_color ?? "#F8FAFC",
						metadata: activateMetadata(data.metadata),
					},
				};
			},
		},
		{
			id: BANNER_VARIANTS.BANNER_9,
			name: "Banner 9 — Carousel hero (animated headline)",
			description:
				"Banner 9 — Full-bleed carousel with pre-title badge, split headline + animated rotating text, programme buttons, CTA / brochure / offer line, accreditation logos and EMI image.",
			...extractMetadataFields(BANNER_9_CONSTANTS),
			componentFields: BANNER_9_CONSTANTS.BANNER_FIELDS_CONFIG,
			showTable: false,
			setAction: setBanner9,
			formComponent: Form_1,
			previewComponent: Banner9,
			selector: (state: PageState) => state.banner_9,
			createMethod: createBanner,
			updateMethod: updateBanner,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						metadata: Array.isArray(data.metadata) ? data.metadata : [],
						image: data.image || "",
						mobile_image: data.mobile_image || "",
						image_alt: data.image_alt || "banner-bg",
						slide_frequency: data.slide_frequency ?? 8000,
						hero_section_height: data.hero_section_height ?? 564,
						hero_container_max_width: data.hero_container_max_width ?? 1114,
						background_color: data.background_color || "rgba(32, 30, 30, 0.8)",
						apply_now_show: isCmsShowFlagOn(data.apply_now_show),
						apply_now_text: data.apply_now_text || "Enroll Now",
						apply_now_link: data.apply_now_link || "",
						apply_now_is_cta: isCmsShowFlagOn(data.apply_now_is_cta, true),
						download_brochure_show: isCmsShowFlagOn(data.download_brochure_show),
						download_brochure_text:
							data.download_brochure_text || "Download Brochure",
						download_brochure_link: data.download_brochure_link || "",
						download_brochure_is_cta: isCmsShowFlagOn(
							data.download_brochure_is_cta,
							true,
						),
						show_lead_form: isCmsShowFlagOn(data.show_lead_form, false),
						enable_lead_form: isCmsShowFlagOn(data.enable_lead_form, false),
						button_color: data.button_color || "#E1473F",
						apply_now_color: data.apply_now_color || "#ffffff",
						carousel_color: data.carousel_color || "#E1473F",
						status: isCmsShowFlagOn(data.status, true),
					},
				};
			},
		},
		{
			id: BANNER_VARIANTS.BANNER_15,
			name: "Banner 15 — Simple hero (title over image)",
			description:
				"Banner 15 — Full-bleed background image with a single overlaid title (text + color).",
			componentFields: BANNER_15_CONSTANTS.BANNER_FIELDS_CONFIG,
			showTable: false,
			setAction: setBanner15,
			formComponent: Form_1,
			previewComponent: Banner15,
			selector: (state: PageState) => state.banner_15,
			createMethod: createBanner,
			updateMethod: updateBanner,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						...data,
						status: data.status !== false,
						title: data.title ?? "",
						image: data.image ?? "",
						mobile_image: data.mobile_image ?? "",
						image_alt: data.image_alt ?? "",
						title_color: data.title_color ?? "#FFFFFF",
						background_color: data.background_color ?? "#FFFFFF",
					},
				};
			},
		},
		{
			id: BANNER_VARIANTS.BANNER_1010,
			name: "Banner 1010 — Simple hero (centered title + side image)",
			description:
				"Banner 1010 — Banner 9's hero layout without the carousel or its extras: a full-bleed background with a single highlighted headline centered in the left column, and a dark right panel carrying a 432×432 hero image (desktop). Desktop background is bundled; mobile background is admin-driven.",
			componentFields: BANNER_1010_CONSTANTS.BANNER_FIELDS_CONFIG,
			showTable: false,
			setAction: setBanner1010,
			formComponent: Form_1,
			previewComponent: Banner1010,
			selector: (state: PageState) => state.banner_1010,
			createMethod: createBanner,
			updateMethod: updateBanner,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						...data,
						status: isCmsShowFlagOn(data.status, true),
						title: data.title ?? "",
						title_color: data.title_color ?? "#FFFFFF",
						title_highlight_color: data.title_highlight_color ?? "#FFD700",
						image: data.image ?? "",
						image_alt: data.image_alt || "banner",
						mobile_image: data.mobile_image ?? "",
						background_color: data.background_color ?? "rgba(3, 7, 18, 1)",
						hero_section_height: data.hero_section_height ?? 564,
						hero_container_max_width: data.hero_container_max_width ?? 1114,
					},
				};
			},
		},
		{
			id: BANNER_VARIANTS.BANNER_10,
			name: "Banner 10",
			description: "Banner 10 - Hero Banner — value-prop column + inline lead form (LeadDrop9)",
			...extractMetadataFields(BANNER_10_CONSTANTS),
			componentFields: BANNER_10_CONSTANTS.BANNER_FIELDS_CONFIG,
			showTable: true,
			setAction: setBanner10,
			formComponent: Form_1,
			previewComponent: Banner10,
			selector: (state: PageState) => state.banner_10,
			createMethod: createBanner,
			updateMethod: updateBanner,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						metadata: activateMetadata(data.metadata),
						metadata_1: activateMetadata(data.metadata_1),
						metadata_2: activateMetadata(data.metadata_2),
						metadata_3: activateMetadata(data.metadata_3),
						background_image: data.background_image || "",
						background_image_mobile: data.background_image_mobile || "",
						background_color: data.background_color || "#50040B",
						// Per-view breadcrumb flags. `??` (not `||`) so an explicit
						// off survives, and the legacy shared flag is the fallback
						// for rows saved before the split.
						show_breadcrumbs: data.show_breadcrumbs ?? false,
						show_breadcrumbs_desktop:
							data.show_breadcrumbs_desktop ?? data.show_breadcrumbs ?? false,
						show_breadcrumbs_mobile:
							data.show_breadcrumbs_mobile ?? data.show_breadcrumbs ?? false,
						eyebrow_badge_bg_color:
							data.eyebrow_badge_bg_color || "rgba(255,255,255,0.14)",
						// `??` so an explicit off survives; missing = on (legacy rows).
						show_eyebrow_badges_mobile: data.show_eyebrow_badges_mobile ?? true,
						title_1: data.title_1 || "India's 1st Online MBA in",
						title_1_color: data.title_1_color || "#F5F5F5",
						// `??` so an explicit off survives; missing = on (legacy rows).
						show_title_1_mobile: data.show_title_1_mobile ?? true,
						title_2: data.title_2 || "Product Management",
						title_2_color: data.title_2_color || "#F5F5F5",
						show_title_2_mobile: data.show_title_2_mobile ?? true,
						subtitle: data.subtitle || "with CFP® Global Certification program",
						subtitle_color: data.subtitle_color || "rgba(255,255,255,0.8)",
						show_subtitle_mobile: data.show_subtitle_mobile ?? true,
						mobile_creative_image: data.mobile_creative_image || "",
						show_mobile_creative_shadow:
							data.show_mobile_creative_shadow ?? false,
						mobile_creative_shadow_color:
							data.mobile_creative_shadow_color || "rgba(0,0,0,0.35)",
						paragraph_variant: data.paragraph_variant || "bullets",
						// `??` so an explicit off survives; missing = on (legacy rows).
						show_paragraph_mobile: data.show_paragraph_mobile ?? true,
						paragraph_text: data.paragraph_text || "",
						paragraph_color: data.paragraph_color || "#FFFFFF",
						bullet_icon: data.bullet_icon || "",
						collaboration_image: data.collaboration_image || "",
						collaboration_image_mobile: data.collaboration_image_mobile || "",
						download_brochure_text: data.download_brochure_text || "Download Brochure",
						button_color: data.button_color || "#FFFFFF",
						cta_text_color: data.cta_text_color || "#222222",
						explore_cta_url: data.explore_cta_url ?? "",
						cta_action: data.cta_action || "download",
						// Trust Builders: per-view flags, legacy shared flag as fallback.
						show_learners: data.show_learners ?? true,
						show_learners_desktop:
							data.show_learners_desktop ?? data.show_learners ?? true,
						show_learners_mobile:
							data.show_learners_mobile ?? data.show_learners ?? true,
						learners_description: data.learners_description || "Join 10k+ alumni",
						learners_description_color: data.learners_description_color || "#FFFFFF",
						show_rating: data.show_rating ?? true,
						show_rating_desktop:
							data.show_rating_desktop ?? data.show_rating ?? true,
						show_rating_mobile:
							data.show_rating_mobile ?? data.show_rating ?? true,
						rating_number: data.rating_number || "4/5",
						rating_number_color: data.rating_number_color || "#FFFFFF",
						rating_text: data.rating_text || "(7.8K ratings)",
						rating_color: data.rating_color || "rgba(255,255,255,0.5)",
						enable_lead_form: data.enable_lead_form ?? true,
						enable_animation: data.enable_animation ?? false,
						status: true,
					},
				};
			},
		},
		{
			id: BANNER_VARIANTS.BANNER_11,
			name: "Banner 11",
			description: "Banner 11 - Middle Banner — bg image + left image + content column + dual CTAs",
			...extractMetadataFields(BANNER_11_CONSTANTS),
			componentFields: BANNER_11_CONSTANTS.BANNER_FIELDS_CONFIG,
			showTable: true,
			setAction: setBanner11,
			formComponent: Form_1,
			previewComponent: Banner11,
			selector: (state: PageState) => state.banner_11,
			createMethod: createBanner,
			updateMethod: updateBanner,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						metadata: activateMetadata(data.metadata),
						background_image: data.background_image || "",
						background_image_mobile: data.background_image_mobile || "",
						background_color: data.background_color || "#D6DE66",
						left_image: data.left_image || "",
						title_1: data.title_1 || "Admissions Open for 2026",
						title_1_color: data.title_1_color || "#081F3D",
						paragraph_text:
							data.paragraph_text ||
							"Join a new generation of business professionals equipped to navigate change, leverage AI, and create impact.",
						paragraph_color: data.paragraph_color || "#081F3D",
						bullet_icon: data.bullet_icon || "",
						download_brochure_text: data.download_brochure_text || "Apply Now",
						button_color: data.button_color || "#081F3D",
						cta_text_color: data.cta_text_color || "#FFFFFF",
						apply_now_text: data.apply_now_text || "Prospectus",
						apply_now_color: data.apply_now_color || "#081F3D",
						apply_now_border_color: data.apply_now_border_color || "#081F3D",
						enable_animation: data.enable_animation ?? false,
						status: true,
					},
				};
			},
		},
		{
			id: BANNER_VARIANTS.BANNER_3000,
			name: "Banner 3000",
			description: "Banner 3000 - Copy of Banner 1 but with shorter height",
			componentFields: BANNER_3000_CONSTANTS.BANNER_FIELDS_CONFIG,
			...extractMetadataFields(BANNER_3000_CONSTANTS),
			showTable: true,
			setAction: setBanner3000,
			formComponent: Form_1,
			previewComponent: Banner3000,
			selector: (state: PageState) => state.banner_3000,
			createMethod: createBanner,
			updateMethod: updateBanner,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						metadata: activateMetadata(data.metadata),
						metadata_1: activateMetadata(data.metadata_1),
						learners_description: data.learners_description || "",
						learners_description_color:
							data.learners_description_color || "#000000",
						learners_status: data.learners_status || false,
						slide_frequency: data.slide_frequency || 5000,
						status: true,
					},
				};
			},
		},
		{
			id: BANNER_VARIANTS.BANNER_1025,
			name: "Banner 1025 — BDU Hero",
			description:
				"Banner 1025 - BDU Hero — slide carousel with left overlay card, CTA and accreditation bar",
			componentFields: BANNER_1025_CONSTANTS.BANNER_FIELDS_CONFIG,
			...extractMetadataFields(BANNER_1025_CONSTANTS),
			showTable: true,
			setAction: setBanner1025,
			formComponent: Form_1,
			previewComponent: Banner1025,
			selector: (state: PageState) => state.banner_1025,
			createMethod: createBanner,
			updateMethod: updateBanner,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						// Not activateMetadata: the widget hides status-false slides itself, so
						// the preview must see the same rows the live page does.
						metadata: Array.isArray(data.metadata) ? data.metadata : [],
						metadata_1: Array.isArray(data.metadata_1) ? data.metadata_1 : [],
						slide_frequency: data.slide_frequency ?? 5000,
						hero_section_height: data.hero_section_height ?? 540,
						gradient_color_1: data.gradient_color_1 || "#13344F",
						gradient_color_2: data.gradient_color_2 || "#77B6EB",
						background_color: data.background_color || "#22405ADB",
						gradient_color_3: data.gradient_color_3 || "#FF0013",
						gradient_color_4: data.gradient_color_4 || "#990013",
						apply_now_show: isCmsShowFlagOn(data.apply_now_show, true),
						apply_now_text: data.apply_now_text || "Enroll Now",
						apply_now_link: data.apply_now_link || "",
						apply_now_is_cta: isCmsShowFlagOn(data.apply_now_is_cta, true),
						apply_now_color: data.apply_now_color || "#FFFFFF",
						horizontal_stripe_color:
							data.horizontal_stripe_color || "#FFFFFF",
						status: isCmsShowFlagOn(data.status, true),
					},
				};
			},
		},
		{
			id: BANNER_VARIANTS.BANNER_1035,
			name: "Banner 1035 — BDU Programme Hero",
			description:
				"Banner 1035 - BDU Programme Hero — slide carousel with an h2 sub-heading, Apply Now + Brochure CTAs and an accreditation bar",
			componentFields: BANNER_1035_CONSTANTS.BANNER_FIELDS_CONFIG,
			...extractMetadataFields(BANNER_1035_CONSTANTS),
			showTable: true,
			setAction: setBanner1035,
			formComponent: Form_1,
			previewComponent: Banner1035,
			selector: (state: PageState) => state.banner_1035,
			createMethod: createBanner,
			updateMethod: updateBanner,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						// Not activateMetadata: the widget hides status-false slides itself, so
						// the preview must see the same rows the live page does.
						metadata: Array.isArray(data.metadata) ? data.metadata : [],
						metadata_1: Array.isArray(data.metadata_1) ? data.metadata_1 : [],
						slide_frequency: data.slide_frequency ?? 5000,
						hero_section_height: data.hero_section_height ?? 540,
						gradient_color_1: data.gradient_color_1 || "#13344F",
						gradient_color_2: data.gradient_color_2 || "#77B6EB",
						background_color: data.background_color || "#22405ADB",
						gradient_color_3: data.gradient_color_3 || "#FF0013",
						gradient_color_4: data.gradient_color_4 || "#990013",
						apply_now_show: isCmsShowFlagOn(data.apply_now_show, true),
						apply_now_text: data.apply_now_text || "Apply Now",
						apply_now_link: data.apply_now_link || "",
						apply_now_is_cta: isCmsShowFlagOn(data.apply_now_is_cta, true),
						apply_now_color: data.apply_now_color || "#FFFFFF",
						apply_now_border_color: data.apply_now_border_color || "#FFFFFF",
						download_brochure_show: isCmsShowFlagOn(
							data.download_brochure_show,
							true,
						),
						download_brochure_text: data.download_brochure_text || "Brochure",
						download_brochure_link: data.download_brochure_link || "",
						download_brochure_is_cta: isCmsShowFlagOn(
							data.download_brochure_is_cta,
							true,
						),
						cta_text_color: data.cta_text_color || "#FFFFFF",
						horizontal_stripe_color:
							data.horizontal_stripe_color || "#FFFFFF",
						status: isCmsShowFlagOn(data.status, true),
					},
				};
			},
		},
    {
      id: BANNER_VARIANTS.BANNER_3001,
      name: "Banner 3001 — KUK Auxiliary Hero",
      description:
        "KUK auxiliary page styled banner — background image behind a white left-to-right scrim, with a vertically centred title and an optional row of link icons",
      componentFields: BANNER_3001_CONSTANTS.BANNER_FIELDS_CONFIG,
      ...extractMetadataFields(BANNER_3001_CONSTANTS),
      showTable: true,
      setAction: setBanner3001,
      formComponent: Form_1,
      previewComponent: Banner3001,
      selector: (state: PageState) => state.banner_3001,
      createMethod: createBanner,
      updateMethod: updateBanner,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            // Not activateMetadata: the widget drops status-false rows itself,
            // so the preview must see the same rows the live page does.
            metadata: Array.isArray(data.metadata) ? data.metadata : [],
            image: data.image || "",
            mobile_image: data.mobile_image || "",
            image_alt: data.image_alt || "",
            title: data.title || "",
            title_color: data.title_color || "#031F44",
            gradient_color_1:
              data.gradient_color_1 || "rgba(255, 255, 255, 0.92)",
            gradient_color_2:
              data.gradient_color_2 || "rgba(255, 255, 255, 0.00)",
            icon_color: data.icon_color || "#031F44",
            hero_section_height: data.hero_section_height ?? 300,
            hero_container_max_width: data.hero_container_max_width ?? 1120,
            status: isCmsShowFlagOn(data.status, true),
          },
        };
      },
    },
    {
      id: BANNER_VARIANTS.BANNER_1051,
      name: "Banner 1051 — KUK Home Hero",
      description:
        "KUK home hero — a slide carousel behind a translucent white card carrying an eyebrow, the university name, a coloured second line, a supporting paragraph and one CTA",
      componentFields: BANNER_1051_CONSTANTS.BANNER_FIELDS_CONFIG,
      ...extractMetadataFields(BANNER_1051_CONSTANTS),
      showTable: true,
      setAction: setBanner1051,
      formComponent: Form_1,
      previewComponent: Banner1051,
      selector: (state: PageState) => state.banner_1051,
      createMethod: createBanner,
      updateMethod: updateBanner,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              // Not activateMetadata: the widget drops status-false rows itself.
              metadata: Array.isArray(data.metadata) ? data.metadata : [],
              status: true,
            },
          },
        };
      },
    },
    {
      id: BANNER_VARIANTS.BANNER_1058,
      name: "Banner 1058 — KUK Home CTA band",
      description:
        "KUK home CTA band — a short full-bleed photograph under a scrim, with an icon, one bold line of copy and a white outline button",
      componentFields: BANNER_1058_CONSTANTS.BANNER_FIELDS_CONFIG,
      ...extractMetadataFields(BANNER_1058_CONSTANTS),
      showTable: true,
      setAction: setBanner1058,
      formComponent: Form_1,
      previewComponent: Banner1058,
      selector: (state: PageState) => state.banner_1058,
      createMethod: createBanner,
      updateMethod: updateBanner,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              status: true,
            },
          },
        };
      },
    },
    {
      id: BANNER_VARIANTS.BANNER_1059,
      name: "Banner 1059 — KUK Home referral band",
      description:
        "KUK home referral band — full-bleed artwork with a { } highlighted headline, a supporting line and a small gradient pill CTA",
      componentFields: BANNER_1059_CONSTANTS.BANNER_FIELDS_CONFIG,
      ...extractMetadataFields(BANNER_1059_CONSTANTS),
      showTable: true,
      setAction: setBanner1059,
      formComponent: Form_1,
      previewComponent: Banner1059,
      selector: (state: PageState) => state.banner_1059,
      createMethod: createBanner,
      updateMethod: updateBanner,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              status: true,
            },
          },
        };
      },
    },
    {
      id: BANNER_VARIANTS.BANNER_1061,
      name: "Banner 1061 — KUK Programme hero",
      description:
        "KUK programme hero — a three-slide carousel where each slide carries its own layout, copy, chips and colours, with Apply and Brochure CTAs and an accreditation rail",
      componentFields: BANNER_1061_CONSTANTS.BANNER_FIELDS_CONFIG,
      ...extractMetadataFields(BANNER_1061_CONSTANTS),
      showTable: true,
      setAction: setBanner1061,
      formComponent: Form_1,
      previewComponent: Banner1061,
      selector: (state: PageState) => state.banner_1061,
      createMethod: createBanner,
      updateMethod: updateBanner,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              status: true,
            },
          },
        };
      },
    },
    {
      id: BANNER_VARIANTS.BANNER_1062,
      name: "Banner 1062 — KUK Programme referral band",
      description:
        "KUK programme referral band — the 1059 artwork band at a larger type scale, followed by a three-column strip of USP tiles",
      componentFields: BANNER_1062_CONSTANTS.BANNER_FIELDS_CONFIG,
      ...extractMetadataFields(BANNER_1062_CONSTANTS),
      showTable: true,
      setAction: setBanner1062,
      formComponent: Form_1,
      previewComponent: Banner1062,
      selector: (state: PageState) => state.banner_1062,
      createMethod: createBanner,
      updateMethod: updateBanner,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              status: true,
            },
          },
        };
      },
    },
    {
      id: BANNER_VARIANTS.BANNER_1068,
      name: "Banner 1068 — KUK Dashboard access band",
      description:
        "KUK dashboard-access band — artwork behind a large icon, one bold line and a bordered CTA",
      componentFields: BANNER_1068_CONSTANTS.BANNER_FIELDS_CONFIG,
      showTable: false,
      setAction: setBanner1068,
      formComponent: Form_1,
      previewComponent: Banner1068,
      selector: (state: PageState) => state.banner_1068,
      createMethod: createBanner,
      updateMethod: updateBanner,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            data: {
              ...data,
              status: true,
            },
          },
        };
      },
    },
    {
      id: BANNER_VARIANTS.BANNER_5004,
      name: "Banner 5004 — KUK Marketing Banner",
      description:
        "KUK full-bleed CTA band — background photo with a heading, one supporting line and a single button, placed either centred on the left or in the bottom-right corner",
      componentFields: BANNER_5004_CONSTANTS.BANNER_FIELDS_CONFIG,
      ...extractMetadataFields(BANNER_5004_CONSTANTS),
      showTable: true,
      setAction: setBanner5004,
      formComponent: Form_1,
      previewComponent: Banner5004,
      selector: (state: PageState) => state.banner_5004,
      createMethod: createBanner,
      updateMethod: updateBanner,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            backvariant: data.backvariant || "variant_1",
            image: data.image || "",
            mobile_image: data.mobile_image || "",
            image_alt: data.image_alt || "",
            background_color: data.background_color || "transparent",
            // Hero-only, and empty means "no scrim" — so no fallback here.
            bleed_background_color: data.bleed_background_color || "",
            horizontal_stripe_color:
              data.horizontal_stripe_color || "rgba(255, 255, 255, 0.80)",
            card_title_color: data.card_title_color || "#101112",
            // Not activateMetadata: the widget drops status-false rows itself.
            metadata_1: Array.isArray(data.metadata_1) ? data.metadata_1 : [],
            hero_section_height: data.hero_section_height ?? 366,
            title: data.title || "",
            title_color: data.title_color || "#FFFFFF",
            description: data.description || "",
            description_color: data.description_color || "#FFFFFF",
            apply_now_show: isCmsShowFlagOn(data.apply_now_show, true),
            apply_now_text: data.apply_now_text || "Get in touch",
            apply_now_is_cta: isCmsShowFlagOn(data.apply_now_is_cta, true),
            apply_now_link: data.apply_now_link || "",
            apply_now_color: data.apply_now_color || "transparent",
            apply_now_border_color: data.apply_now_border_color || "#FFFFFF",
            cta_text_color: data.cta_text_color || "#FFFFFF",
            // Hero-only inline lead form. Both flags are forwarded because the
            // widget accepts either; without them the admin preview could never
            // paint the form, however the editor set the toggle.
            show_lead_form: isCmsShowFlagOn(data.show_lead_form, false),
            enable_lead_form: isCmsShowFlagOn(data.enable_lead_form, false),
            // Empty, not defaulted: the gradient only applies when BOTH stops are
            // set, so a fallback here would fill every preview button.
            gradient_color_3: data.gradient_color_3 || "",
            gradient_color_4: data.gradient_color_4 || "",
            status: isCmsShowFlagOn(data.status, true),
          },
        };
      },
    },
    {
      id: BANNER_VARIANTS.BANNER_6000,
      name: "Banner 6000 — BDU Auxiliary Hero",
      description:
        "Banner 6000 - BDU auxiliary page hero — one static background with a left gradient card holding the title and an accreditation bar",
      componentFields: BANNER_6000_CONSTANTS.BANNER_FIELDS_CONFIG,
      ...extractMetadataFields(BANNER_6000_CONSTANTS),
      showTable: true,
      setAction: setBanner6000,
      formComponent: Form_1,
      previewComponent: Banner6000,
      selector: (state: PageState) => state.banner_6000,
      createMethod: createBanner,
      updateMethod: updateBanner,
      preparePreviewData: ({ section, reduxState, isSelected }: any) => {
        const data = getPreviewData(section, reduxState, isSelected);
        return {
          data: {
            // Not activateMetadata: the widget hides status-false badges itself,
            // so the preview must see the same rows the live page does.
            metadata: Array.isArray(data.metadata) ? data.metadata : [],
            image: data.image || "",
            mobile_image: data.mobile_image || "",
            image_alt: data.image_alt || "",
            title: data.title || "",
            title_color: data.title_color || "#FFFFFF",
            hero_section_height: data.hero_section_height ?? 418,
            background_color: data.background_color || "#22405ADB",
            gradient_color_1: data.gradient_color_1 || "#13344F",
            gradient_color_2: data.gradient_color_2 || "#77B6EB",
            carousel_color: data.carousel_color || "#FFFFFF",
            status: isCmsShowFlagOn(data.status, true),
          },
        };
      },
    },
	],
	[SECTION_TYPES.BANNER_VIDEO]: [
		{
			id: BANNER_VIDEO_VARIANTS.BANNER_VIDEO_1,
			name: "Banner Video 1",
			description: "Banner Video 1",
			componentFields: BANNER_VIDEO_1_CONSTANTS.BANNER_VIDEO_FIELDS_CONFIG,
			...extractMetadataFields(BANNER_VIDEO_1_CONSTANTS),
			showTable: true,
			setAction: setBannerVideo1,
			formComponent: Form_1,
			previewComponent: BannerVideo1,
			selector: (state: PageState) => state.banner_video_1,
			createMethod: createBannerVideo,
			updateMethod: updateBannerVideo,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						...data,
						metadata: activateMetadata(data.metadata),
						status: true,
						desktop_background_color:
							data.desktop_background_color ?? "#FFFFFF",
						mobile_background_color: data.mobile_background_color ?? "#FFFFFF",
						backvariant: data.backvariant ?? "variant_1",
						gradient_color_1:
							data.gradient_color_1 ?? "rgba(0, 212, 235, 0.33)",
						gradient_color_2: data.gradient_color_2 ?? "rgba(0, 0, 0, 0.66)",
						gradient_color_3: data.gradient_color_3 ?? "rgba(0, 0, 0, 0.66)",
						gradient_color_4:
							data.gradient_color_4 ?? "rgba(83, 69, 195, 0.46)",
						slide_frequency: data.slide_frequency ?? 5000,
					},
				};
			},
		},
	],
	[SECTION_TYPES.WALK_THROUGH]: [
		{
			id: WALK_THROUGH_VARIANTS.WALK_THROUGH_1,
			name: "Walk Through 1",
			description: "Walk Through 1",
			componentFields: WALK_THROUGH_1_CONSTANTS.WALK_THROUGH_FIELDS_CONFIG,
			...extractMetadataFields(WALK_THROUGH_1_CONSTANTS),
			showTable: false,
			setAction: setWalkThrough1,
			formComponent: Form_1,
			previewComponent: WalkThrough1,
			selector: (state: PageState) => state.walk_through_1,
			createMethod: createWalkThrough,
			updateMethod: updateWalkThrough,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						...data,
						status: data.status !== false,
						desktop_bg_image: data.desktop_bg_image ?? "",
						mobile_bg_image: data.mobile_bg_image ?? "",
						alt_image: data.alt_image ?? "Campus walkthrough",
						title_prefix:
							data.title_prefix ??
							"IIT Kharagpur marks 75 Years of Academic Excellence and Leadership",
						title_highlight: data.title_highlight ?? "",
						title_suffix: data.title_suffix ?? "",
						title_highlight_color: data.title_highlight_color ?? "#FFFFFF",
						video_link: data.video_link ?? "",
						cta_label: data.cta_label ?? "View Video",
						overlay_color: data.overlay_color ?? "#352D59",
						overlay_opacity:
							data.overlay_opacity !== undefined ? data.overlay_opacity : 0.9,
					},
				};
			},
		},
	],
	[SECTION_TYPES.LIFE_MILESTONE]: [
		{
			id: LIFE_MILESTONE_VARIANTS.LIFE_MILESTONE_1,
			name: "Life Milestone 1",
			description: "Life Milestone 1",
			componentFields: LIFE_MILESTONE_1_CONSTANTS.LIFE_MILESTONE_FIELDS_CONFIG,
			...extractMetadataFields(LIFE_MILESTONE_1_CONSTANTS),
			showTable: true,
			setAction: setLifeMilestone1,
			formComponent: Form_1,
			previewComponent: LifeMilestone1,
			selector: (state: PageState) => state.life_milestone_1,
			createMethod: createLifeMilestone,
			updateMethod: updateLifeMilestone,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				const defaultRows = [
					{
						meta_milestone_label: "GROW",
						meta_heading: "From Potential to Power",
						meta_description:
							"Step into a world where your skills, knowledge, and confidence grow together — preparing you to take on global careers, leadership roles, and every opportunity life throws your way.",
						meta_priority: 1,
						meta_status: 1,
					},
					{
						meta_milestone_label: "TRANSFORM",
						meta_heading: "From Learner to Leader",
						meta_description:
							"IIT Kharagpur is more than a degree — it is your launchpad. You will graduate as a confident, future-ready professional with the mindset to lead and the skills to deliver.",
						meta_priority: 2,
						meta_status: 1,
					},
					{
						meta_milestone_label: "CREATE",
						meta_heading: "Your Ideas. Your Impact.",
						meta_description:
							"Whether you are building your career, launching a venture, or creating something the world has not seen yet — here, you do not just follow paths; you create them.",
						meta_priority: 3,
						meta_status: 1,
					},
				];
				const metadata =
					Array.isArray(data.metadata) && data.metadata.length > 0
						? data.metadata
						: defaultRows;
				return {
					data: {
						...data,
						status: data.status !== false,
						title_prefix: data.title_prefix ?? "Life after ",
						title_highlight: data.title_highlight ?? "IIT Kharagpur",
						title_suffix: data.title_suffix ?? "",
						title_highlight_color: data.title_highlight_color ?? "#FFC600",
						background_color: data.background_color ?? "#291973",
						image_frame_color: data.image_frame_color ?? "#E1473F",
						hero_image: data.hero_image ?? "",
						hero_image_alt: data.hero_image_alt ?? "",
						timeline_color: data.timeline_color ?? "#807070",
						auto_rotate_seconds:
							data.auto_rotate_seconds !== undefined
								? data.auto_rotate_seconds
								: 0,
						metadata,
					},
				};
			},
		},
		{
			id: LIFE_MILESTONE_VARIANTS.LIFE_MILESTONE_2,
			name: "Life Milestone 2 — Hero + copy",
			description: "Life Milestone 2 — Hero + copy",
			componentFields:
				LIFE_MILESTONE_2_CONSTANTS.LIFE_MILESTONE_2_FIELDS_CONFIG,
			showTable: false,
			setAction: setLifeMilestone2,
			formComponent: Form_1,
			previewComponent: LifeMilestone2,
			selector: (state: PageState) => state.life_milestone_2,
			createMethod: createLifeMilestone,
			updateMethod: updateLifeMilestone,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						...data,
						status: data.status !== false,
						background_color: data.background_color ?? "#291973",
						block_main_title:
							data.block_main_title ?? "On-Campus Graduation Ceremony",
						block_subtitle: data.block_subtitle ?? "at IIT Kharagpur",
						block_body:
							data.block_body ??
							"The programme concludes with an on-campus award ceremony at IIT Kharagpur, where participants receive their certificates from the Programme Director and Institute leadership.",
						block_cta_label: data.block_cta_label ?? "Start Learning Now",
						block_cta_url: data.block_cta_url ?? "",
						block_feature_label:
							data.block_feature_label ?? "Faculty Interaction",
						hero_image: data.hero_image ?? "",
						hero_image_alt: data.hero_image_alt ?? "",
					},
				};
			},
		},
		{
			id: LIFE_MILESTONE_VARIANTS.LIFE_MILESTONE_3,
			name: "Life Milestone 3 — App / LMS",
			description: "Life Milestone 3 — title + copy + CTA + checklist + image",
			componentFields:
				LIFE_MILESTONE_3_CONSTANTS.LIFE_MILESTONE_3_FIELDS_CONFIG,
			...extractMetadataFields(LIFE_MILESTONE_3_CONSTANTS),
			showTable: true,
			setAction: setLifeMilestone3,
			formComponent: Form_1,
			previewComponent: LifeMilestone3,
			selector: (state: PageState) => state.life_milestone_3,
			createMethod: createLifeMilestone,
			updateMethod: updateLifeMilestone,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				const defaultRows = [
					{ meta_description: "Faculty Interaction", meta_priority: 1, meta_status: 1 },
					{ meta_description: "Live Online Sessions", meta_priority: 2, meta_status: 1 },
					{ meta_description: "Industry Projects", meta_priority: 3, meta_status: 1 },
				];
				const metadata =
					Array.isArray(data.metadata) && data.metadata.length > 0
						? data.metadata
						: defaultRows;
				return {
					data: {
						...data,
						status: data.status !== false,
						background_color: data.background_color ?? "#291973",
						block_main_title:
							data.block_main_title ?? "Learn Anywhere, Anytime",
						block_subtitle: data.block_subtitle ?? "with our Mobile App",
						block_body:
							data.block_body ??
							"Access your courses, live sessions, and learning material on the go — wherever you are.",
						block_cta_label: data.block_cta_label ?? "Start Learning Now",
						block_cta_url: data.block_cta_url ?? "",
						hero_image: data.hero_image ?? "",
						hero_image_mobile: data.hero_image_mobile ?? "",
						hero_image_alt: data.hero_image_alt ?? "",
						metadata,
					},
				};
			},
		},
	],
	[SECTION_TYPES.ENROLLMENTS]: [
		{
			id: ENROLLMENTS_VARIANTS.ENROLLMENTS_1,
			name: "Enrollments 1",
			description: "Enrollment 1 - framed banner",
			componentFields: ENROLLMENTS_1_CONSTANTS.ENROLLMENTS_FIELDS_CONFIG,
			showTable: false,
			setAction: setEnrollments1,
			formComponent: Form_1,
			previewComponent: Enrollments1,
			selector: (state: PageState) => state.enrollments_1,
			createMethod: createEnrollments,
			updateMethod: updateEnrollments,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						...data,
						status: data.status !== false,
						headline:
							data.headline ?? "Ready to Take the Next Step? Enroll Today!",
						cta_label: data.cta_label ?? "Apply Now",
						cta_url: data.cta_url ?? "",
						open_in_new_tab: data.open_in_new_tab !== false,
						outer_background_color: data.outer_background_color ?? "#FDE1D1",
						inner_background_color: data.inner_background_color ?? "#DB5803",
						headline_color: data.headline_color ?? "#FFFFFF",
						cta_background_color: data.cta_background_color ?? "#FFFFFF",
						cta_text_color: data.cta_text_color ?? "#DB5803",
					},
				};
			},
		},
		{
			id: ENROLLMENTS_VARIANTS.ENROLLMENTS_2,
			name: "Program Enrollments",
			description:
				"Enrollments 2 - Lead popup",
			componentFields: ENROLLMENTS_2_CONSTANTS.ENROLLMENTS_FIELDS_CONFIG,
			showTable: false,
			setAction: setEnrollments2,
			formComponent: Form_1,
			previewComponent: Enrollments2,
			selector: (state: PageState) => state.enrollments_2,
			createMethod: createEnrollments,
			updateMethod: updateEnrollments,
			dynamicOptionsConfig: {
				courses: {
					fetch: getActiveCourses,
					transform: (course: { id?: number; program_name?: string }) => ({
						value: String(course.id ?? ""),
						label: course.program_name ?? String(course.id ?? ""),
					}),
				},
			},
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						...data,
						status: data.status !== false,
						headline_primary:
							data.headline_primary ?? "A full-stack AI engineering program",
						headline_accent:
							data.headline_accent ?? "to ship real-world AI systems",
						body_paragraph_1: data.body_paragraph_1 ?? "",
						body_paragraph_2: data.body_paragraph_2 ?? "",
						body_paragraph_3: data.body_paragraph_3 ?? "",
						enable_lead_popup:
							data.enable_lead_popup === undefined ||
								data.enable_lead_popup === null
								? true
								: Boolean(data.enable_lead_popup),
						// Opt-in, so an unset/NULL column must read as OFF — `isCmsShowFlagOn`
						// also handles the 0/1 and "0"/"1" shapes MySQL hands back.
						enable_lead_form: isCmsShowFlagOn(data.enable_lead_form, false),
						bound_course_id:
							data.bound_course_id != null && data.bound_course_id !== ""
								? String(data.bound_course_id)
								: "",
					},
				};
			},
		},
		{
			id: ENROLLMENTS_VARIANTS.ENROLLMENTS_3,
			name: "Enrollments 3 - Image Banner",
			description:
				"Enrollments 3 - Image Banner",
			componentFields: ENROLLMENTS_3_CONSTANTS.ENROLLMENTS_FIELDS_CONFIG,
			showTable: false,
			setAction: setEnrollments3,
			formComponent: Form_1,
			previewComponent: Enrollments3,
			selector: (state: PageState) => state.enrollments_3,
			createMethod: createEnrollments,
			updateMethod: updateEnrollments,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						...data,
						status: data.status !== false,
						title: data.title ?? "Why Pursue {this Programme}",
						description:
							data.description ?? "Take the next step in your career",
						cta_label: data.cta_label ?? "Apply Now",
						cta_url: data.cta_url ?? "",
						open_in_new_tab: data.open_in_new_tab !== false,
						desktop_image: data.desktop_image ?? "",
						mobile_image: data.mobile_image ?? "",
						background_color: data.background_color ?? "#FFFFFF",
						overlay_color: data.overlay_color ?? "#000000",
						overlay_opacity:
							data.overlay_opacity === undefined ||
								data.overlay_opacity === null ||
								data.overlay_opacity === ""
								? "0.5"
								: data.overlay_opacity,
						title_color: data.title_color ?? "#FFFFFF",
						title_highlight_color: data.title_highlight_color ?? "#FFD700",
						description_color: data.description_color ?? "#FFFFFF",
						cta_background_color: data.cta_background_color ?? "#FFFFFF",
						cta_text_color: data.cta_text_color ?? "#DB5803",
						enable_lead_popup: isCmsShowFlagOn(data.enable_lead_popup, true),
					},
				};
			},
		},
		{
			id: ENROLLMENTS_VARIANTS.ENROLLMENTS_4,
			name: "Enrollments 4 - Flip Cards Banner",
			description:
				"Enrollments 4 - Flip Cards Banner",
			componentFields: ENROLLMENTS_4_CONSTANTS.ENROLLMENTS_FIELDS_CONFIG,
			...extractMetadataFields(ENROLLMENTS_4_CONSTANTS),
			showTable: false,
			setAction: setEnrollments4,
			formComponent: Form_1,
			previewComponent: Enrollments4,
			selector: (state: PageState) => state.enrollments_4,
			createMethod: createEnrollments,
			updateMethod: updateEnrollments,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						...data,
						status: data.status !== false,
						title: data.title ?? "Key {advantages} of this programme",
						cta_label: data.cta_label ?? "Get Started",
						cta_url: data.cta_url ?? "",
						open_in_new_tab: data.open_in_new_tab !== false,
						desktop_image: data.desktop_image ?? "",
						mobile_image: data.mobile_image ?? "",
						background_color: data.background_color ?? "#FFFFFF",
						title_color: data.title_color ?? "#0A0A0A",
						title_highlight_color: data.title_highlight_color ?? "#DB5803",
						cta_background_color: data.cta_background_color ?? "#DB5803",
						cta_text_color: data.cta_text_color ?? "#FFFFFF",
						enable_lead_popup: isCmsShowFlagOn(data.enable_lead_popup, true),
						metadata: activateMetadata(data.metadata),
					},
				};
			},
		},
		{
			id: ENROLLMENTS_VARIANTS.ENROLLMENTS_5,
			name: "Enrollments 5 - About Course",
			description: "Enrollments 5 - About Course",
			componentFields: ENROLLMENTS_5_CONSTANTS.ENROLLMENTS_FIELDS_CONFIG,
			...extractMetadataFields(ENROLLMENTS_5_CONSTANTS),
			showTable: false,
			setAction: setEnrollments5,
			formComponent: Form_1,
			previewComponent: Enrollments5,
			selector: (state: PageState) => state.enrollments_5,
			createMethod: createEnrollments,
			updateMethod: updateEnrollments,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						...data,
						status: data.status !== false,
						title: data.title ?? "About this {Programme}",
						description: data.description ?? "",
						cta_label: data.cta_label ?? "Apply Now",
						cta_url: data.cta_url ?? "",
						open_in_new_tab: data.open_in_new_tab !== false,
						background_color: data.background_color ?? "#FFFFFF",
						title_color: data.title_color ?? "#0A0A0A",
						title_highlight_color: data.title_highlight_color ?? "#DB5803",
						description_color: data.description_color ?? "#0A0A0A",
						body_text_color: data.body_text_color ?? "#0A0A0A",
						accent_color: data.accent_color ?? "#DB5803",
						cta_background_color: data.cta_background_color ?? "#DB5803",
						cta_text_color: data.cta_text_color ?? "#FFFFFF",
						enable_lead_popup: isCmsShowFlagOn(data.enable_lead_popup, true),
						metadata: activateMetadata(data.metadata),
					},
				};
			},
		},
	],
};

export const HEADER_SECTION_VARIANTS = {
	[SECTION_TYPES.HEADER]: [
		{
			id: HEADER_VARIANTS.HEADER_1,
			name: "Header 1",
			description: "Header 1",
			componentFields: HEADER_1_CONSTANTS.HEADER_FIELDS_CONFIG,
			...extractMetadataFields(HEADER_1_CONSTANTS),
			showTable: false,
			setAction: setHeader1,
			formComponent: Form_1,
			previewComponent: Header1,
			selector: (state: PageState) => state.header_1,
			createMethod: createHeader,
			updateMethod: updateHeader,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							display_name: data.display_name || "",
							featured_program_title: data.featured_program_title || "",
							name: data.name || "",
							phone_number: data.phone_number || "",
							promotional_text: data.promotional_text || "",
							short_name: data.short_name || "",
							whatsApp_link: data.whatsApp_link || "",
							background_color: data.background_color || "#ffffff",
							logo: data.logo || "",
							promotional_text_bg_color:
								data.promotional_text_bg_color || "#BE2330",
							phone_icon: data.phone_icon || "",
							button_name: data.button_name || "",
							button_link: data.button_link || "",
							button_color: data.button_color || "#BE2330",
							status: true,
						},
					},
				};
			},
		},
		{
			id: HEADER_VARIANTS.HEADER_2,
			name: "Header 2",
			description: "Header 2",
			componentFields: HEADER_2_CONSTANTS.HEADER_FIELDS_CONFIG,
			...extractMetadataFields(HEADER_2_CONSTANTS),
			showTable: false,
			setAction: setHeader2,
			formComponent: Form_1,
			previewComponent: Header2,
			selector: (state: PageState) => state.header_2,
			createMethod: createHeader,
			updateMethod: updateHeader,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							background_color: data.background_color || "#ffffff",
							logo: data.logo || "",
							logo_alt_text: data.logo_alt_text || "",
							status: true,
						},
					},
				};
			},
		},
		{
			id: HEADER_VARIANTS.HEADER_3,
			name: "Header 3",
			description: "Header 3 - Promotional Banner with Gradient",
			componentFields: HEADER_3_CONSTANTS.HEADER_FIELDS_CONFIG,
			...extractMetadataFields(HEADER_3_CONSTANTS),
			showTable: false,
			setAction: setHeader3,
			formComponent: Form_1,
			previewComponent: Header3,
			selector: (state: PageState) => state.header_3,
			createMethod: createHeader,
			updateMethod: updateHeader,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							promotional_text:
								data.promotional_text ||
								"Admissions are closing on 31st Jan. Avail up to 25% scholarship (valid only for this batch)",
							promotional_text_color: data.promotional_text_color || "#FFFFFF",
							auto_scroll_speed: data.auto_scroll_speed || "20000",
							gradient_color_1: data.gradient_color_1 || "#342B7C",
							gradient_color_2: data.gradient_color_2 || "#00D4EB",
							phone_icon: data.phone_icon || "",
							phone_icon_alt: data.phone_icon_alt || "Phone",
							phone_number: data.phone_number || "+91 7973615772",
							phone_number_text_color:
								data.phone_number_text_color || "#FFFFFF",
							button_name: data.button_name || "WhatsApp Chat",
							button_link: data.button_link || "#whatsapp",
							button_icon: data.button_icon || "",
							button_icon_alt: data.button_icon_alt || "WhatsApp",
							button_color: data.button_color || "#4CAF51",
							button_text_color: data.button_text_color || "#ffffff",
							status: true,
						},
					},
				};
			},
		},
		{
			id: HEADER_VARIANTS.HEADER_4,
			name: "Header 4 - ContactInfoBar",
			description: "Header 4 - Contact Info Bar",
			componentFields: HEADER_4_CONSTANTS.HEADER_FIELDS_CONFIG,
			...extractMetadataFields(HEADER_4_CONSTANTS),
			showTable: true,
			setAction: setHeader4,
			formComponent: Form_1,
			previewComponent: Header4,
			selector: (state: PageState) => state.header_4,
			createMethod: createHeader,
			updateMethod: updateHeader,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						isPreview: true,
						data: {
							status: data.status !== false,
							promotional_text:
								data.promotional_text ||
								"Admissions closing soon — {Apply now} for early benefits",
							promotional_text_color: data.promotional_text_color || "#FFFFFF",
							link_color: data.link_color || "#FFFFFF",
							gradient_color_1: data.gradient_color_1 || "#342B7C",
							gradient_color_2: data.gradient_color_2 || "#00D4EB",
							background_gradient_angle:
								data.background_gradient_angle || "90deg",
							link_href: data.link_href || "",
							link_trailing_image: data.link_trailing_image || "",
							link_trailing_image_alt: data.link_trailing_image_alt || "",
							header_status: data.header_status !== false,
						},
					},
				};
			},
		},
		{
			id: HEADER_VARIANTS.HEADER_5,
			name: "Header 5 - IIT - ContactInfoBar",
			description: "Header 5 - IIT - Contact Info Bar",
			componentFields: HEADER_5_CONSTANTS.HEADER_FIELDS_CONFIG,
			...extractMetadataFields(HEADER_5_CONSTANTS),
			showTable: true,
			setAction: setHeader5,
			formComponent: Form_1,
			previewComponent: Header5,
			selector: (state: PageState) => state.header_5,
			createMethod: createHeader,
			updateMethod: updateHeader,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							background_color: data.background_color || "#291973",
							pill_chip_background_colour:
								data.pill_chip_background_colour ||
								data.pill_background_color ||
								"#ffffff1a",
							pill_background_color:
								data.pill_background_color ||
								data.pill_chip_background_colour ||
								"#ffffff1a",
							label_text_color: data.label_text_color || "#FFFFFF",
							phone_text_color: data.phone_text_color || "#FFFFFF",
							phone_icon: data.phone_icon || "",
							phone_icon_alt: data.phone_icon_alt || "Phone",
							metadata: data.metadata || [],
							contact_items_json: data.contact_items_json,
							show_on_mobile: data.show_on_mobile ?? false,
							sticky_to_top: data.sticky_to_top === true,
							show_logo: data.show_logo,
							logo_image: data.logo_image || "",
							show_apply_button: data.show_apply_button,
							apply_button_text: data.apply_button_text || "Apply",
							status: true,
						},
						isPreview: true,
					},
				};
			},
		},
		{
			id: HEADER_VARIANTS.HEADER_6,
			name: "Header 6 - Featured pill",
			description: "Header 6 - Featured pill",
			componentFields: HEADER_6_CONSTANTS.HEADER_FIELDS_CONFIG,
			...extractMetadataFields(HEADER_6_CONSTANTS),
			showTable: false,
			setAction: setHeader6,
			formComponent: Form_1,
			previewComponent: Header6,
			selector: (state: PageState) => state.header_6,
			createMethod: createHeader,
			updateMethod: updateHeader,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							featured_pill_show: data.featured_pill_show !== false,
							featured_pill_text:
								data.featured_pill_text || "Limited Seats Available",
							featured_pill_bg_color: data.featured_pill_bg_color || "#DB5803",
							featured_pill_border_color:
								data.featured_pill_border_color || "#DB5803",
							featured_pill_text_color:
								data.featured_pill_text_color || "#FFFFFF",
							featured_pill_sticky: data.featured_pill_sticky !== false,
							status: data.status !== false,
						},
						isPreview: true,
					},
				};
			},
		},
		{
			id: HEADER_VARIANTS.HEADER_7,
			name: "Header 7 - Contact bar + WhatsApp pill",
			description:
				"Header 7 - Contact bar with WhatsApp pill and configurable dynamic widgets",
			componentFields: HEADER_7_CONSTANTS.HEADER_FIELDS_CONFIG,
			...extractMetadataFields(HEADER_7_CONSTANTS),
			showTable: true,
			setAction: setHeader7,
			formComponent: Form_1,
			previewComponent: Header7,
			selector: (state: PageState) => state.header_7,
			createMethod: createHeader,
			updateMethod: updateHeader,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							background_color: data.background_color || "#291973",
							pill_chip_background_colour:
								data.pill_chip_background_colour ||
								data.pill_background_color ||
								"#ffffff1a",
							pill_background_color:
								data.pill_background_color ||
								data.pill_chip_background_colour ||
								"#ffffff1a",
							label_text_color: data.label_text_color || "#FFFFFF",
							phone_text_color: data.phone_text_color || "#FFFFFF",
							metadata: data.metadata || [],
							show_on_mobile: data.show_on_mobile ?? false,
							sticky_to_top: data.sticky_to_top === true,
							show_logo: data.show_logo,
							logo_image: data.logo_image || "",
							whatsapp_show: data.whatsapp_show !== false,
							whatsapp_number: data.whatsapp_number || "",
							whatsapp_link: data.whatsapp_link || "",
							whatsapp_button_text:
								data.whatsapp_button_text || "WhatsApp Chat",
							whatsapp_button_color: data.whatsapp_button_color || "#4CAF51",
							whatsapp_button_text_color:
								data.whatsapp_button_text_color || "#FFFFFF",
							whatsapp_icon: data.whatsapp_icon || "",
							button_name: data.button_name || "",
							button_link: data.button_link || "",
							button_color: data.button_color || "",
							button_text_color: data.button_text_color || "",
							status: true,
						},
						isPreview: true,
					},
				};
			},
		},
		{
			id: HEADER_VARIANTS.HEADER_1046,
			name: "Header 1046 - BDU contact strip",
			description:
				"Header 1046 - Slate contact strip with right-aligned phone + WhatsApp pills (desktop only)",
			componentFields: HEADER_1046_CONSTANTS.HEADER_FIELDS_CONFIG,
			...extractMetadataFields(HEADER_1046_CONSTANTS),
			showTable: true,
			setAction: setHeader1046,
			formComponent: Form_1,
			previewComponent: Header1046,
			selector: (state: PageState) => state.header_1046,
			createMethod: createHeader,
			updateMethod: updateHeader,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					data: {
						data: {
							background_color: data.background_color || "#354D6C",
							pill_background_color:
								data.pill_background_color || "rgba(255, 255, 255, 0.10)",
							label_text_color: data.label_text_color || "#FFFFFF",
							phone_text_color: data.phone_text_color || "#FFFFFF",
							phone_icon: data.phone_icon || "",
							phone_icon_alt: data.phone_icon_alt || "Phone",
							metadata: data.metadata || [],
							show_on_mobile: data.show_on_mobile ?? false,
							sticky_to_top: data.sticky_to_top !== false,
							whatsapp_show: data.whatsapp_show !== false,
							whatsapp_number: data.whatsapp_number || "",
							whatsapp_link: data.whatsapp_link || "",
							whatsapp_button_text:
								data.whatsapp_button_text || "WhatsApp Chat",
							whatsapp_button_color:
								data.whatsapp_button_color || "rgba(76, 175, 81, 0.70)",
							whatsapp_button_text_color:
								data.whatsapp_button_text_color || "#FFFFFF",
							whatsapp_icon: data.whatsapp_icon || "",
							status: true,
						},
						isPreview: true,
					},
				};
			},
		},
		{
			id: HEADER_VARIANTS.PROMO_PILL,
			name: "Promo Pill",
			description:
				"Rounded badge with configurable text, colours and optional link; can stay pinned over the page while scrolling",
			componentFields: PROMO_PILL_1_CONSTANTS.PROMO_PILL_FIELDS_CONFIG,
			...extractMetadataFields(PROMO_PILL_1_CONSTANTS),
			showTable: false,
			setAction: setHeader5000,
			formComponent: Form_1,
			previewComponent: PromoPill1,
			selector: (state: PageState) => state.header_5000,
			createMethod: createHeader,
			updateMethod: updateHeader,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					// The editor pane renders the pill inline; only the live page pins it.
					isPreview: true,
					data: {
						data: {
							featured_pill_text: data.featured_pill_text || "",
							featured_pill_text_color:
								data.featured_pill_text_color || "#222023",
							featured_pill_bg_color: data.featured_pill_bg_color || "#F4F217",
							featured_pill_border_color:
								data.featured_pill_border_color || "#F4F217",
							featured_pill_sticky:
								data.featured_pill_sticky !== undefined
									? data.featured_pill_sticky
									: true,
							featured_pill_sticky_offset:
								data.featured_pill_sticky_offset || "72",
							featured_pill_enable_animation:
								data.featured_pill_enable_animation !== undefined
									? data.featured_pill_enable_animation
									: true,
							featured_pill_link_url: data.featured_pill_link_url || "",
							status: true,
						},
					},
				};
			},
		},
		{
			id: HEADER_VARIANTS.HEADER_5001,
			name: "Header 5001 - BDU site header",
			description:
				"BDU site header — white bar, logo left; phone line, WhatsApp button and Apply CTA right (phone + WhatsApp are desktop-only)",
			componentFields: HEADER_5001_CONSTANTS.HEADER_FIELDS_CONFIG,
			...extractMetadataFields(HEADER_5001_CONSTANTS),
			showTable: false,
			setAction: setHeader5001,
			formComponent: Form_1,
			previewComponent: Header5001,
			selector: (state: PageState) => state.header_5001,
			createMethod: createHeader,
			updateMethod: updateHeader,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					isPreview: true,
					data: {
						data: {
							background_color: data.background_color || "#FFFFFF",
							show_bar_shadow: data.show_bar_shadow ?? false,
							sticky_to_top: data.sticky_to_top ?? true,
							show_on_mobile: data.show_on_mobile ?? true,
							show_logo: data.show_logo ?? true,
							logo_image: data.logo_image || "",
							logo_alt_text: data.logo_alt_text || "",
							phone_number: data.phone_number || "",
							phone_text_color: data.phone_text_color || "#272323",
							phone_icon: data.phone_icon || "",
							phone_icon_alt: data.phone_icon_alt || "",
							whatsapp_show: data.whatsapp_show ?? true,
							whatsapp_number: data.whatsapp_number || "",
							whatsapp_link: data.whatsapp_link || "",
							whatsapp_button_text: data.whatsapp_button_text || "WhatsApp Chat",
							whatsapp_button_color: data.whatsapp_button_color || "#4CAF51",
							whatsapp_button_text_color:
								data.whatsapp_button_text_color || "#FFFFFF",
							whatsapp_icon: data.whatsapp_icon || "",
							apply_button_text: data.apply_button_text || "",
							button_color: data.button_color || "#BE2330",
							button_text_color: data.button_text_color || "#FFFFFF",
							status: true,
						},
					},
				};
			},
		},
		{
			id: HEADER_VARIANTS.HEADER_5015,
			name: "Header 5015 — KUK Site Header",
			description:
				"KUK landing-page header — white bar with the logo left and a green WhatsApp link, a phone line and an Apply CTA right (the WhatsApp link and phone line are desktop-only)",
			componentFields: HEADER_5015_CONSTANTS.HEADER_FIELDS_CONFIG,
			...extractMetadataFields(HEADER_5015_CONSTANTS),
			showTable: false,
			setAction: setHeader5015,
			formComponent: Form_1,
			previewComponent: Header5015,
			selector: (state: PageState) => state.header_5015,
			createMethod: createHeader,
			updateMethod: updateHeader,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					isPreview: true,
					data: {
						data: {
							...data,
							status: isCmsShowFlagOn(data.status, true),
						},
					},
				};
			},
		},
		{
			id: HEADER_VARIANTS.HEADER_5016,
			name: "Header 5016 — KUK Promotional Marquee",
			description:
				"KUK promotional strip — one line of promo copy scrolling continuously on a tinted bar, with motion disabled for visitors who ask for reduced motion",
			componentFields: HEADER_5016_CONSTANTS.HEADER_FIELDS_CONFIG,
			...extractMetadataFields(HEADER_5016_CONSTANTS),
			showTable: false,
			setAction: setHeader5016,
			formComponent: Form_1,
			previewComponent: Header5016,
			selector: (state: PageState) => state.header_5016,
			createMethod: createHeader,
			updateMethod: updateHeader,
			preparePreviewData: ({ section, reduxState, isSelected }: any) => {
				const data = getPreviewData(section, reduxState, isSelected);
				return {
					isPreview: true,
					data: {
						data: {
							...data,
							status: isCmsShowFlagOn(data.status, true),
						},
					},
				};
			},
		},
	],
};
